import React from 'react'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next'
import dynamic from 'next/dynamic'
import { getUser, getUserProjects } from '@/helpers/api'
import { UserProfilePageQuery } from '@/interfaces/filters'
import { Project } from '@/interfaces/projects'
import { ListPageProps } from '@/interfaces/pages'
import useQueryParamsHandler from '@/hooks/useQueryParamsHandler'
import { SeoHead } from '@/components/seo/SeoHead'
import { getCanonicalLink } from '@/helpers/seo'
import { getFiltersFromQuery } from '@/helpers/filters'
import { getUserHref } from '@/helpers/uri'
import { User } from '@/interfaces/users'
import UserProfileBar from '@/components/user/userProfile/UserProfileBar'
import ProjectsViewWrapper from '@/components/projects/ProjectsViewWrapper'
import Subheader from '@/components/subheader/Subheader'
import { isMobileDevice } from '@/helpers/helpers'

interface UserProfilePageProps extends ListPageProps {
  user: User
  projects: Project[]
}

export async function getServerSideProps(
  context: GetServerSidePropsContext
): Promise<GetServerSidePropsResult<UserProfilePageProps>> {
  const { username, ...params } = context.query as UserProfilePageQuery

  const user = await getUser(username, context.req)
  if (!user) {
    return {
      notFound: true
    }
  }

  const { filters, shouldRedirect } = getFiltersFromQuery(params, context.req, { time: true })

  // user profile page should not have time filter
  delete params.time
  delete filters.time

  if (shouldRedirect) {
    const filtersQueryString = Object.keys(filters)
      .map((key) => `${key}=${filters[key]}`)
      .join('&')
      .toString()
    return {
      redirect: {
        destination: `/${username}/?${filtersQueryString}`,
        permanent: false
      }
    }
  }

  const { items: projects, pagingMetadata } = await getUserProjects({ username, ...params, ...filters }, context.req)

  let title: string =
    username + ' on SimScale | SimScale' + (pagingMetadata?.page ? ` | Page ${pagingMetadata?.page}` : '')
  let pageTitle: string = username + "'s profile"
  if (context.req.cookies?.username === username) {
    title = 'Your SimScale Profile | SimScale' + (pagingMetadata?.page ? ` | Page ${pagingMetadata?.page}` : '')
    pageTitle = 'Your profile'
  }

  const description =
    'SimScale simulation projects by ' + username + (pagingMetadata?.page ? ` | Page ${pagingMetadata?.page}` : '')

  const canonicalLink = getCanonicalLink(`/projects${getUserHref(username)}`, { ...params, ...filters })

  const isMobile = isMobileDevice(context.req?.headers['user-agent'] || '')

  return {
    props: {
      title,
      description,
      pageTitle,
      user,
      projects,
      pagingMetadata,
      canonicalLink,
      isMobile,
      ...params,
      ...filters,
      ...(await serverSideTranslations(context.locale, [
        'common',
        'projects',
        'header',
        'subheader',
        'filters',
        'users'
      ]))
    }
  }
}

const UserProfilePage = ({
  title,
  description,
  pageTitle,
  user,
  projects,
  viewMode,
  sortBy,
  search,
  pagingMetadata,
  canonicalLink,
  isMobile
}: UserProfilePageProps): React.ReactElement => {
  useQueryParamsHandler({ ignore: { time: true } })

  const Sidebar = dynamic(() => import('@/components/sidebar/Sidebar'), {
    ssr: false
  })

  return (
    <>
      <SeoHead title={title} description={description} canonicalLink={canonicalLink} />
      <UserProfileBar user={user} />
      <Container>
        <Grid container spacing={6}>
          <Grid item xs={12} md={8} lg={9}>
            <Subheader
              title={pageTitle}
              viewMode={viewMode}
              sortByFilter={sortBy}
              searchString={search}
              showSearch
              showSortByFilters
              showViewMode={!isMobile}
              onIndexPage
            />
            <ProjectsViewWrapper
              projects={projects}
              viewMode={isMobile ? 'listView' : viewMode}
              gridColumns={3}
              pagingMetadata={pagingMetadata}
            />
          </Grid>
          <Grid item xs={12} md={4} lg={3} sx={{ display: { xs: 'none', sm: 'none', md: 'block' } }}>
            <Sidebar user={user} />
          </Grid>
        </Grid>
      </Container>
    </>
  )
}

export default UserProfilePage
